import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { ChatFileLink } from '@chat-emp/shared';

import { resolveChatFiles } from '../lib/facade.js';

const LINK_LIFE_MS = 5 * 60_000;
const REFRESH_MARGIN_MS = 30_000;
const MIN_REFRESH_MS = 60_000;
const MAX_IDS_PER_CALL = 100;
// TODO(AI-3341): drop the polling once the backend patches the message when a thumbnail lands.
export const THUMB_POLL_MS = 5_000;
export const THUMB_POLLS = 4;

/** `link`: undefined until a resolve succeeds; null once the server has left the id out. */
interface Entry {
  link?: ChatFileLink | null;
  staleAt: number;
}

const known = new Map<string, Entry>();
const queued = new Map<string, Set<string>>();
const asking = new Set<string>();
const lastRetry = new Map<string, number>();
const thumbPolls = new Map<string, number>();
const listeners = new Set<() => void>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;

/** A file id is only ever resolved within its own channel. */
const keyOf = (channelId: string, fileId: string) => `${channelId}\n${fileId}`;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

/** Bounded both ways, because the server's clock and ours disagree by an unknown amount. */
function entryFor(link: ChatFileLink | null): Entry {
  if (!link) return { link, staleAt: Number.POSITIVE_INFINITY };
  const now = Date.now();
  const expires = Date.parse(link.expiresAt);
  const ttl = Number.isFinite(expires) ? expires - now : 0;
  const life = Math.min(Math.max(ttl, MIN_REFRESH_MS + REFRESH_MARGIN_MS), LINK_LIFE_MS);
  return { link, staleAt: now + life - REFRESH_MARGIN_MS };
}

function ask(channelId: string, fileId: string, force = false): void {
  const key = keyOf(channelId, fileId);
  if (asking.has(key)) return;
  if (!force && (known.get(key)?.staleAt ?? 0) > Date.now()) return;
  const ids = queued.get(channelId) ?? new Set<string>();
  ids.add(fileId);
  queued.set(channelId, ids);
  flushTimer ??= setTimeout(() => void flush(), 0);
}

async function flush(): Promise<void> {
  flushTimer = undefined;
  const calls: { channelId: string; fileIds: string[] }[] = [];
  for (const [channelId, ids] of queued) {
    const all = [...ids];
    for (let i = 0; i < all.length; i += MAX_IDS_PER_CALL) {
      calls.push({ channelId, fileIds: all.slice(i, i + MAX_IDS_PER_CALL) });
    }
  }
  queued.clear();
  await Promise.all(calls.map(resolve));
}

async function resolve(call: { channelId: string; fileIds: string[] }): Promise<void> {
  const keys = call.fileIds.map((id) => keyOf(call.channelId, id));
  keys.forEach((key) => asking.add(key));
  try {
    const { files } = await resolveChatFiles(call);
    call.fileIds.forEach((id, i) => known.set(keys[i]!, entryFor(files[id] ?? null)));
  } catch {
    const retryAt = Date.now() + MIN_REFRESH_MS;
    for (const key of keys) known.set(key, { link: known.get(key)?.link, staleAt: retryAt });
  } finally {
    keys.forEach((key) => asking.delete(key));
    notify();
  }
}

export function resetChatFileLinks(): void {
  clearTimeout(flushTimer);
  flushTimer = undefined;
  for (const store of [known, queued, asking, lastRetry, thumbPolls]) store.clear();
  notify();
}

export interface ChatFileLinkState {
  link?: ChatFileLink;
  /** The server left it out: deleted, or never in this channel. */
  gone: boolean;
  /** For an `<img>` that failed to load. At most once a minute, so a broken file cannot loop. */
  retry: () => void;
}

export function useChatFileLink(
  channelId: string,
  fileId: string,
  awaitThumb = false,
): ChatFileLinkState {
  const key = keyOf(channelId, fileId);
  const entry = useSyncExternalStore(subscribe, () => known.get(key));

  useEffect(() => {
    ask(channelId, fileId);
    if (!entry || entry.link === null) return;
    const refresh = () => ask(channelId, fileId, true);
    const timer = setTimeout(
      () => {
        if (document.hidden) document.addEventListener('visibilitychange', refresh, { once: true });
        else refresh();
      },
      Math.max(entry.staleAt - Date.now(), 0),
    );
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [channelId, fileId, entry]);

  useEffect(() => {
    const polls = thumbPolls.get(key) ?? 0;
    if (!awaitThumb || !entry?.link || entry.link.thumbUrl || polls >= THUMB_POLLS) return;
    const timer = setTimeout(() => {
      thumbPolls.set(key, polls + 1);
      ask(channelId, fileId, true);
    }, THUMB_POLL_MS);
    return () => clearTimeout(timer);
  }, [awaitThumb, channelId, fileId, key, entry]);

  const retry = useCallback(() => {
    const now = Date.now();
    if (!known.get(key)?.link || now - (lastRetry.get(key) ?? -Infinity) < MIN_REFRESH_MS) return;
    lastRetry.set(key, now);
    ask(channelId, fileId, true);
  }, [channelId, fileId, key]);

  return { link: entry?.link ?? undefined, gone: entry?.link === null, retry };
}
